import React, { useEffect, useState } from 'react';
import {
  EditBase,
  SimpleForm,
  TextInput,
  BooleanInput,
  useNotify,
  useRedirect,
  useUpdate,
  required,
  useDataProvider,
  SelectInput,
  email,
  useEditContext,
} from "react-admin";
import Box from "@mui/material/Box";
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';

// 自定义组件来监听邮箱变化并自动填充用户名
const AutoFillUserName = () => {
  const { watch, setValue } = useFormContext();
  const email = watch('email');
  const userName = watch('userName');

  useEffect(() => {
    if (email && email.includes('@')) {
      const username = email.split('@')[0];
      setValue('userName', username, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [email, setValue]);
    return null;
};

const UserEditForm: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const { id } = useParams();
  const { record } = useEditContext();

  const [roles, setRoles] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // -----------------------------
  // 加载角色和部门列表
  // -----------------------------
  useEffect(() => {
    Promise.all([
      dataProvider.getList('permissionRoles', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      }),
      dataProvider.getList('permissionDepartment', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
    ])
      .then(([rolesRes, departmentsRes]) => {
        setRoles(rolesRes.data);
        setDepartments(departmentsRes.data);
        setLoading(false);
      })
      .catch(() => {
        notify('Error loading data', { type: 'warning' });
        setLoading(false);
      });
  }, [dataProvider, notify]);

  if (loading) return <span>Loading...</span>;
  if (!record) return <span>No user record found</span>;

  // -----------------------------
  // 保存
  // -----------------------------
  const handleSave = async (data: any) => {
    try {
      const payload: any = {
        userName: data.userName,
        email: data.email,
        departmentId: data.departmentId,
        roleId: data.roleId,
        isActive: data.isActive,
      };

      // 密码为空则不修改
      if (data.password && data.password.trim()) {
        // 使用 Web Crypto 做 SHA-256 哈希
        const enc = new TextEncoder();
        const pwBuf = enc.encode(data.password);
        const hashBuf = await crypto.subtle.digest('SHA-256', pwBuf);
        const hashArray = Array.from(new Uint8Array(hashBuf));
        const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        payload.password = passwordHash;
      }

      await update('permissionUsers', { id: id, data: payload });

      notify('User updated successfully', { type: 'success' });
      setTimeout(() => {
        redirect('list', 'permissionUsers');
      }, 500);
    } catch (error) {
      notify('Update failed', { type: 'error' });
    }
  };

  return (
    <SimpleForm sx={{ backgroundColor: '#ffffff', padding: '36px' }} onSubmit={handleSave}>
      <AutoFillUserName />
      <Box display="flex" flexWrap="wrap" gap={2}>

        {/* 邮箱 */}
        <Box flex="1 1 48%">
          <TextInput
            source="email"
            label="Mailbox"
            fullWidth
            validate={[required(), email()]}
          />
        </Box>

        {/* 用户名（不允许修改） */}
        <Box flex="1 1 48%">
          <TextInput
            source="userName"
            label="User Name"
            fullWidth
            validate={[required()]}
            // disabled
          />
        </Box>

        {/* 修改密码（可空） */}
        <Box flex="1 1 48%">
          <TextInput
            source="password"
            label="New Password (optional)"
            type="password"
            fullWidth
            helperText="Leave empty to keep current password"
          />
        </Box>

        {/* 部门下拉列表 */}
        <Box flex="1 1 48%">
          <SelectInput
            source="departmentId"
            label="Department"
            choices={departments}
            optionText="departmentName"
            optionValue="id"
            fullWidth
            validate={[required()]}
          />
        </Box>

        {/* 角色下拉列表 */}
        <Box flex="1 1 48%">
          <SelectInput
            source="roleId"
            label="Role"
            choices={roles}
            optionText="roleName"
            optionValue="id"
            fullWidth
            validate={[required()]}
          />
        </Box>

        {/* 是否启用 */}
        <Box flex="1 1 48%">
          <BooleanInput
            source="isActive"
            label="Active"
            fullWidth
          />
        </Box>

      </Box>

    </SimpleForm>
  );
};

const UserEdit: React.FC = () => (
  <EditBase resource="permissionUsers">
    <UserEditForm />
  </EditBase>
);

export default UserEdit;
